import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

interface FiltersState {
  supplierId: string;
  openPoDays: number;
  salCommittedEndDate: string;
  factoryFilter: string;
}

interface StockPlanningFiltersProps {
  filters: FiltersState;
  onFiltersChange: (filters: FiltersState) => void;
}

export default function StockPlanningFilters({ filters, onFiltersChange }: StockPlanningFiltersProps) {
  const [suppliers, setSuppliers] = useState<{ id: number; name: string; supplier_code: string }[]>([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const res = await callOffAPI.getSuppliers();
        setSuppliers(res.data);
      } catch {
        // Silently fail — suppliers are optional filters
      }
    };
    loadSuppliers();
  }, []);

  const filteredSuppliers = useMemo(() => {
    const term = supplierSearch.toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(s => s.name.toLowerCase().includes(term));
  }, [suppliers, supplierSearch]);

  const selectedSupplierName = useMemo(() => {
    if (!filters.supplierId) return '';
    const s = suppliers.find(s => String(s.id) === filters.supplierId);
    return s?.name || '';
  }, [filters.supplierId, suppliers]);

  return (
    <div className="flex items-end gap-4">
      <div className="space-y-1 relative">
        <Label className="text-xs">Supplier</Label>
        <div className="relative">
          <input
            type="text"
            placeholder="Search suppliers..."
            value={filters.supplierId ? selectedSupplierName : supplierSearch}
            onChange={(e) => {
              setSupplierSearch(e.target.value);
              onFiltersChange({ ...filters, supplierId: '' });
              setSupplierDropdownOpen(true);
            }}
            onFocus={() => setSupplierDropdownOpen(true)}
            className="w-52 px-3 py-2 pr-8 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          {filters.supplierId && (
            <button
              type="button"
              onClick={() => {
                onFiltersChange({ ...filters, supplierId: '' });
                setSupplierSearch('');
              }}
              className="absolute right-8 top-2 p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {supplierDropdownOpen && filteredSuppliers.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
            {filteredSuppliers.slice(0, 50).map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  onFiltersChange({ ...filters, supplierId: String(s.id) });
                  setSupplierSearch('');
                  setSupplierDropdownOpen(false);
                }}
                className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
        {supplierDropdownOpen && (
          <div className="fixed inset-0 z-40" onClick={() => setSupplierDropdownOpen(false)} />
        )}
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Open PO Days</Label>
        <Input
          type="number"
          className="w-24"
          value={filters.openPoDays}
          onChange={(e) => onFiltersChange({ ...filters, openPoDays: parseInt(e.target.value) || 7 })}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">SAL Committed End Date</Label>
        <Input
          type="date"
          className="w-40"
          value={filters.salCommittedEndDate}
          onChange={(e) => onFiltersChange({ ...filters, salCommittedEndDate: e.target.value })}
        />
      </div>
    </div>
  );
}
